/**
 * Step-by-step connector setup guides — data only.
 * Ported verbatim from classic public/app.js PROVIDER_GUIDES / CRED_UNLOCKS.
 * {domain} markers are turned into https links by the dialog renderer.
 */

export const PROVIDER_GUIDES = {
  github: {
    title: 'Where to get a GitHub token',
    steps: [
      'Open {github.com} and log in',
      'Click your profile picture in the top-right corner, then click "Settings"',
      'On the Settings page, scroll all the way down the left-hand menu and click "Developer settings" (it\'s the last item)',
      'Click "Personal access tokens", then click "Tokens (classic)"',
      'Click the "Generate new token" button, then "Generate new token (classic)"',
      'Under "Select scopes", check the box next to "repo"',
      'Scroll down and click the green "Generate token" button',
      'Copy the token shown on screen (it starts with ghp_) — GitHub will not show it to you again once you leave this page',
    ],
  },
  revenuecat: {
    title: 'Where to get a RevenueCat key',
    steps: [
      'Open {app.revenuecat.com} and log in',
      'If you have more than one project, click the one you want to connect',
      'Click the gear icon (⚙️) — this opens "Project settings"',
      'Click "API keys" in the left-hand menu',
      'Look for the section called "Secret API keys" — NOT "Public API keys" (that one won\'t work here)',
      'Click "+ New" to create one',
      'Copy the key shown — it starts with sk_',
    ],
  },
  mixpanel: {
    title: 'Where to get Mixpanel credentials',
    steps: [
      'Open your Mixpanel project and note the URL host — mixpanel.com = United States, eu.mixpanel.com = EU, in.mixpanel.com = India (you must pick that region in the form)',
      'Click the gear icon (⚙️), usually bottom-left, to open "Project Settings"',
      'Click "Service Accounts" in the left-hand menu',
      'Click "Create Service Account"',
      'Type any name you like for it (this name is just a label — it\'s not the username you\'ll actually use)',
      'Mixpanel now shows you two values on screen: a "Username" and a "Secret" — copy BOTH of these right now, the Secret is never shown again after this',
      'Still on Project Settings, click "Overview" — copy the "Project ID" number shown there too (you\'ll need username, secret, project ID, and region)',
    ],
  },
  posthog: {
    title: 'Where to get a PostHog personal API key',
    steps: [
      'Open {us.posthog.com/settings/user-api-keys} (or eu.posthog.com if your project is on EU Cloud)',
      'Click "Create personal API key"',
      'Choose only read scopes — Query Read and project read (do not grant write scopes)',
      'Optionally scope the key to the single project you want Bonnie to query',
      'Copy the key immediately — it starts with phx_ and PostHog will not show it again',
      'Open Project settings and copy the numeric Project ID',
      'In Bonnie, pick US, EU, or Self-hosted to match your instance (self-hosted needs the https URL of your PostHog host — not a *.i.posthog.com ingestion host)',
    ],
  },
  firebase: {
    title: 'Where to get Firebase / BigQuery credentials',
    steps: [
      'Open {console.cloud.google.com} and log in (this is Google Cloud, a different site from Firebase itself)',
      'At the top, make sure the project selector shows the same project as your Firebase app',
      'Enable the BigQuery API for that project: APIs & Services → Library → search "BigQuery API" → Enable (event queries fail with "BigQuery is not enabled" / missing bigquery.jobs.create until this is on)',
      'In the left-hand menu, click "IAM & Admin", then click "Service Accounts"',
      'Click "+ Create Service Account" (or click an existing one if you already have one to use)',
      'Give it any name, then click "Create and Continue"',
      'Grant TWO roles: "BigQuery Job User" (needed to run queries / bigquery.jobs.create) AND "BigQuery Data Viewer" (read the analytics dataset). Data Viewer alone is not enough',
      'Click "Continue" and "Done", then open the service account → "Keys" → "Add Key" → "Create new key" → JSON',
      'A file downloads to your computer automatically — don\'t rename or edit it',
      'Come back to the form and paste the ENTIRE contents of that downloaded file into the box labeled "Paste full JSON key file" — the fields below it will fill themselves in',
      'One value is NOT in that file: open {console.firebase.google.com} → gear icon → "Project settings" → "Integrations" tab → find "BigQuery" — it shows a dataset name like analytics_1234567890. Type that into the "BigQuery Dataset ID" field yourself',
      'If recheck fails on jobs.create: enable BigQuery API on that project and add Job User to the same service account email — you usually do not need a new JSON key',
      'If chat can run default top-events but custom SQL gets 403 Access Denied on events_*: open BigQuery → your analytics_* dataset → Sharing, and grant that same service account "BigQuery Data Viewer" (table-level). Also always query as `project_id.dataset_id.events_*`, not bare analytics_….events_*',
    ],
  },
  appsflyer: {
    title: 'Where to get AppsFlyer credentials',
    steps: [
      'Log into your AppsFlyer account',
      'Click your account icon or email address in the top-right corner',
      'Click "Security center"',
      'Find "AppsFlyer API tokens", then click "Manage your AppsFlyer API tokens"',
      'Click "Create Token"',
      'Choose "API" as the token type, then click "Create"',
      'Copy the long token shown immediately — it will never be shown again, and only an account admin can do this step',
      'This one token works for every app on the account, but you also need the "App ID" of the specific app you\'re connecting — find it in that app\'s own page in the AppsFlyer dashboard (it looks like id123456789 for iOS, or com.example.app for Android)',
    ],
  },
  applovin: {
    title: 'Where to get your AppLovin Report Key',
    steps: [
      'Log into the AppLovin dashboard',
      'Click your account name in the top-right corner',
      'Select "Keys" from the dropdown',
      'Copy the "Report Key" value — this is the credential Bonnie uses to read advertiser (user-acquisition) reporting',
      'The same Report Key covers every app and campaign on your AppLovin account',
    ],
  },
  clickup: {
    title: 'Where to get a ClickUp token',
    steps: [
      'Open {clickup.com} and log in',
      'Click your profile picture in the top-right corner, then click "Settings"',
      'In the left-hand menu, click "Apps"',
      'Under "API Token" (not "Create new app"), click "Generate" or "Regenerate"',
      'Click "Copy" — the token starts with pk_ and never expires. Paste that into Bonnie. Do not use an OAuth app client_id/secret — those need a separate /v2/oauth/token exchange and are not what this connector expects.',
      'With this token Bonnie can create/update tasks & subtasks, upload attachments, comments, checklists, tags, custom fields, time tracking, Docs, Chat, and the rest of the ClickUp API your account can access.',
    ],
  },
  'google-analytics': {
    title: 'Where to get Google Analytics 4 credentials',
    steps: [
      'Open {console.cloud.google.com} and log in',
      'At the top, make sure the project selector shows the project you want to use (or create a new one)',
      'In the left-hand menu, click "APIs & Services", then "Enabled APIs & services". Search for "Google Analytics Data API" and click "Enable" if it isn\'t already',
      'In the left-hand menu, click "IAM & Admin", then click "Service Accounts"',
      'Click "+ Create Service Account", give it any name, click "Create and Continue", then just click "Continue" and "Done" (no special role needed here — access is granted inside Google Analytics itself, in the next steps)',
      'Click on the service account you just created, then click the "Keys" tab',
      'Click "Add Key", then "Create new key", choose "JSON", then click "Create" — a file downloads to your computer',
      'Open that downloaded file and copy the "client_email" value (looks like something@your-project.iam.gserviceaccount.com)',
      'Open {analytics.google.com} and open the GA4 property you want to connect',
      'Click "Admin" (bottom-left gear icon), then under "Property", click "Property Access Management" (you need to be a Property Admin or Editor to do this)',
      'Click the blue "+" button, then "Add users"',
      'Paste in that client_email address, set the role to "Viewer", then click "Add"',
      'Come back to the form and paste the ENTIRE contents of the downloaded JSON file into the box labeled "Paste full JSON key file" — the fields below it fill themselves in',
      'One value is NOT in that file: back in GA4 Admin, click "Property Settings" — copy the "Property ID" number shown there (this is NOT the same as the project ID) and type it into the "GA4 Property ID" field',
    ],
  },
  'google-play': {
    title: 'Where to get Google Play API credentials',
    steps: [
      'Open {console.cloud.google.com} and log in, then pick (or create) the GCP project you want to use',
      'In the left-hand menu, click "APIs & Services", then "Enabled APIs & services". Search for "Google Play Android Developer API" and click "Enable" if it isn\'t already',
      'Click "IAM & Admin", then "Service Accounts", then "+ Create Service Account"',
      'Give it any name, click "Create and Continue", then just click "Done" (no role needed here — access is granted inside Play Console itself)',
      'Click on the service account you just made, then the "Keys" tab',
      'Click "Add Key" → "Create new key" → choose "JSON" → "Create" — a file downloads to your computer',
      'Open that downloaded file and copy the "client_email" value (looks like name@project.iam.gserviceaccount.com)',
      'Open {play.google.com/console} → "Users and permissions" → "Invite new users" → paste that client_email and grant it "View app information" (or higher) — WITHOUT this step the API will reject your key as "not authorized"',
      'Come back to the form and paste the ENTIRE contents of the downloaded JSON file into the box labeled "Paste full JSON key file" — the fields below it fill themselves in',
      'One value is NOT in that file: your app\'s "Package name" (the applicationId, e.g. com.example.app) — find it in Play Console → your app, or in your build.gradle, and type it into the "Package Name" field',
      'For sales/financial data only: in Play Console → Users and permissions, also grant this client_email "View financial data", then copy your "Developer account ID" (19 digits — Settings → Developer account → Account details) into the "Account ID" field',
    ],
  },
  'app-store-connect': {
    title: 'Where to get App Store Connect API credentials',
    steps: [
      'Open {appstoreconnect.apple.com} and log in with an Account Holder or Admin account (a developer role can\'t create API keys)',
      'Click "Users and Access" in the top menu',
      'Click the "Integrations" tab, then under "App Store Connect API" click "Team Keys"',
      'Click the "+" button to create a new key, give it a name (e.g. "BonnieOS"), and set its access to "App Manager" or "Sales" (needs Sales & Trends for revenue/download reports)',
      'Click "Generate" — this downloads a .p8 private key file. Apple only lets you download this key ONCE, so keep it safe',
      'On the same page, copy the "Key ID" (a 10-character code next to the key you just made)',
      'Also copy your "Issuer ID" (a UUID shown near the top of the Integrations page)',
      'Open the downloaded .p8 file in a text editor and copy its ENTIRE contents, including the BEGIN and END PRIVATE KEY lines',
      'For revenue/downloads/subscription reports you also need your "Vendor Number": App Store Connect → "Payments and Financial Reports" → the 8-digit number at the top',
      'Come back to the form and paste each value into its matching field',
    ],
  },
  appfigures: {
    title: 'Where to get your AppFigures Personal Access Token',
    steps: [
      'Open {appfigures.com/developers/keys} and log in',
      'Click "Make me an app" (or create a new API client) and give it a name like "BonnieOS"',
      'Choose the data scopes you need — at least products, sales/revenue reports, ratings, and reviews (a 403 usually means the token is revoked or the client lacked these scopes)',
      'Click to create the client, then open its details',
      'Click "Create Personal Access Token" and copy the token immediately — AppFigures will not show it again',
      'If Bonnie shows AppFigures 403: paste a freshly created PAT into Connectors → AppFigures and save — recheck cannot repair a rejected token. Also confirm the AppFigures account is not billing-locked',
      'Optional but recommended: in AppFigures, open the product you want Bonnie to focus on and copy its numeric Product ID — paste that into the optional Product ID field so reports stay scoped to one app',
      'Come back to the form and paste the Personal Access Token (and Product ID if you have it)',
    ],
  },
  'apple-search-ads': {
    title: 'Where to get Apple Search Ads API credentials',
    steps: [
      'Open {searchads.apple.com} and sign in with a user that has the API role (invite an API user from Account Settings if needed)',
      'On your machine, generate an ES256 key pair (private .pem/.p8 + matching public key). Keep the private key — Apple never stores it',
      'Sign in as that API user → Account Settings → API → paste the public key → Generate API Client',
      'Copy Client ID, Team ID, and Key ID immediately (Apple shows them once). Also copy your Organization ID from account settings',
      'Come back to the form and paste Client ID, Team ID, Key ID, Organization ID, and the full private key PEM (BEGIN/END lines included)',
    ],
  },
  'google-ads': {
    title: 'Where to get Google Ads API credentials',
    steps: [
      'Create a Google Cloud service account and download its JSON key (client_email, private_key, private_key_id)',
      'Open {ads.google.com/aw/apicenter} (Tools → API Center) and copy your Developer Token',
      'Copy your 10-digit Google Ads Customer ID (top-right of the Google Ads UI, the number under the account name)',
      'In your Google Ads account, go to Tools → Access & Security → Users and add the service account email with Standard access',
      'If this is a manager (MCC) account querying a client account, also fill the optional Login Customer ID with the manager ID',
      'Come back to the form and paste each value into its matching field',
    ],
  },
};

/** Providers that have credential fields but no step-by-step guide yet. */
export const NO_GUIDE = {
  'amazon-reports': true,
  sharepoint: true,
  'mongo-readonly': true,
  'sql-readonly': true,
  slack: true,
  stripe: true,
  admob: true,
};

export const CRED_UNLOCKS = {
  'google-analytics': { depth: 35, lines: ['Active users and retention by cohort', 'Funnel drop-off across your key flows', 'Event and screen breakdowns by platform'] },
  firebase: { depth: 35, lines: ['Active users and retention by cohort', 'Funnel drop-off across your key flows', 'Event and screen breakdowns by platform'] },
  mixpanel: { depth: 35, lines: ['Active users and retention by cohort', 'Funnel drop-off across your key flows', 'Event and screen breakdowns by platform'] },
  posthog: { depth: 35, lines: ['Active users and retention by cohort', 'Funnel drop-off across your key flows', 'Event trends and property breakdowns'] },
  'sql-readonly': { depth: 35, lines: ['Raw event tables via SQL', 'Custom aggregations Bonnie cannot infer from GA4', 'Warehouse-backed funnels'] },
  github: { depth: 25, lines: ['Tie releases and commits to what changed in the numbers', 'The difference between “churn rose” and “churn rose after 2.4.1”'] },
  revenuecat: { depth: 25, lines: ['Trial → paid conversion', 'Churn and LTV by cohort'] },
  stripe: { depth: 25, lines: ['Revenue by plan', 'Failed payments against retention'] },
  applovin: { depth: 15, lines: ['User-acquisition spend, installs and CPA by campaign', 'CTR and conversion rate by country, ad or creative set'] },
  'google-ads': { depth: 15, lines: ['Campaign / ad-group / keyword spend', 'Clicks, impressions, CTR, and conversions', 'Acquisition CPA against retention'] },
  appfigures: { depth: 25, lines: ['Cross-store downloads and sales', 'Revenue trends across Apple / Google / Amazon', 'Ratings distribution and recent store reviews'] },
  'app-store-connect': { depth: 25, lines: ['App Store installs, ratings and reviews', 'Proceeds and units by territory', 'Version adoption from the store'] },
  'google-play': { depth: 25, lines: ['Play Console installs and ratings', 'Store listing performance', 'Crash and ANR context alongside reviews'] },
  'amazon-reports': { depth: 25, lines: ['Amazon Appstore sales and downloads', 'Subscription and IAP reports', 'Marketplace performance'] },
};

/**
 * Short "Where to find these" summary from the first two guide steps.
 * @param {{ title?: string, steps?: string[] }|null|undefined} guide
 * @returns {string}
 */
export function credFindCopy(guide) {
  if (!guide || !Array.isArray(guide.steps) || !guide.steps.length) {
    return 'Open the provider’s admin console, create a scoped read-only key, and paste it below. We never save an untested credential.';
  }
  return guide.steps.slice(0, 2).map((s) => s.replace(/\{([\w.-]+\.[a-z]{2,}(?:\/[^\s{}]*)?)\}/gi, '$1')).join('. ');
}

/**
 * @param {string} slug
 * @returns {boolean}
 */
export function hasGuideCoverage(slug) {
  return Boolean(PROVIDER_GUIDES[slug]) || Boolean(NO_GUIDE[slug]);
}
